import useSWR from "swr";

import { pushNotification } from "../stores/hooks/useToasts";
import { reduxStore } from "../stores/reduxStore";
import { Token, authSlice } from "../stores/slices/authSlice";
import { loadScript } from "../util/loadScript";
import { parseToken } from "../util/parseToken";

let pendingUpgrade: Promise<Token> | null = null;
let isSessionExpired = false;
const unverifiedTokenIds = new Set<string>();

const loadToken = async (): Promise<Token> => {
  const dealerUrlCode = window.dealerAccountInfoPayload?.dealerUrlCode;

  await loadScript("tw-token", `/${dealerUrlCode ?? "unknown"}/token.js`);

  const token = parseToken();
  if (!token) {
    throw new Error("Token is not valid");
  }
  return token;
};

const fetchToken = (): Promise<Token> => {
  if (!pendingUpgrade) {
    pendingUpgrade = loadToken().finally(() => {
      pendingUpgrade = null;
    });
  }
  return pendingUpgrade;
};

const replaceToken = (token: Token): void => {
  const action = authSlice.actions.replaceToken(token);
  reduxStore.dispatch(action);
};

export const expireSession = (): void => {
  if (isSessionExpired) {
    return;
  }
  isSessionExpired = true;
  const { auth } = reduxStore.getState();
  replaceToken({
    ...auth,
    tokenIsAuthenticated: false,
  });
  pushNotification({
    message: "toast.sessionExpired",
  });
};

export const restoreSession = (): void => {
  isSessionExpired = false;
};

export const verifyToken = (tokenId: string): void => {
  unverifiedTokenIds.delete(tokenId);
};

export const upgradeToken = async (
  rejectedTokenId: string
): Promise<Token | null> => {
  if (unverifiedTokenIds.has(rejectedTokenId)) {
    expireSession();
    return null;
  }
  if (isSessionExpired) {
    return null;
  }

  const token = await fetchToken().catch(() => null);
  if (!token || isSessionExpired) {
    return null;
  }

  unverifiedTokenIds.add(token.tokenId);
  replaceToken(token);
  return token;
};

const refreshToken = async (): Promise<Token | null> => {
  const token = await fetchToken().catch(() => null);
  if (token) {
    replaceToken(token);
    restoreSession();
  }
  return token;
};

export const useUpgradeToken = (): void => {
  useSWR("upgradeToken", refreshToken, {
    refreshInterval: 1000 * 60 * 60,
    revalidateOnFocus: false,
    revalidateOnMount: false,
  });
};
